import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  AutoComplete,
  Button,
  Checkbox,
  Form,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { DocumentNode } from 'graphql';
import debounce from 'lodash/debounce';
import { GET_STORES } from 'api/store/queries';
import {
  AdminStoresOrderBy,
  SortDirection,
  UserRole,
} from 'api/graphql-global-types';
import { errorNotification, successNotification } from 'ui/Notification';
import styles from '../../SocialMediaIntegrationSettings.module.scss';

const { Text, Title } = Typography;

const STORE_SEARCH_ROLES = [
  UserRole.Athlete,
  UserRole.ContentCreator,
  UserRole.Organization,
  UserRole.Customer,
];

type AllowedUser = {
  id: string;
  firstName: string;
  lastName: string;
  storeDetails?: {
    storeName?: string | null;
  } | null;
};

type SocialMediaFlowSetting = {
  isEnabled: boolean;
  allowedUsers: AllowedUser[];
};

type StoreSearchData = {
  adminStores?: {
    entities?: AllowedUser[];
  };
};

type AutoCompleteOption = {
  value: string;
  label: string;
};

type SocialMediaFlowSettingFormProps = {
  title: string;
  description: string;
  checkboxLabel: string;
  query: DocumentNode;
  mutation: DocumentNode;
  queryField: string;
};

const toStoreOptions = (entities?: AllowedUser[]): AutoCompleteOption[] =>
  entities?.map((store) => ({
    value: store.id,
    label:
      store.storeDetails?.storeName || `${store.firstName} ${store.lastName}`,
  })) ?? [];

const useStoreAutocomplete = (isIdSearch: boolean) => {
  const [options, setOptions] = useState<AutoCompleteOption[]>([]);
  const [searchStores, { data, loading, error }] =
    useLazyQuery<StoreSearchData>(GET_STORES, { fetchPolicy: 'network-only' });

  const search = useMemo(
    () =>
      debounce((value: string) => {
        if (!value.trim()) {
          setOptions([]);
          return;
        }

        searchStores({
          variables: {
            storeRoles: STORE_SEARCH_ROLES,
            input: isIdSearch
              ? { storeIds: [value], limit: 1 }
              : {
                  searchTerm: value,
                  orderBy: AdminStoresOrderBy.storeName,
                  direction: SortDirection.ASC,
                  limit: 10,
                },
          },
        });
      }, 300),
    [isIdSearch, searchStores]
  );

  useEffect(() => () => search.cancel(), [search]);

  useEffect(() => {
    setOptions(toStoreOptions(data?.adminStores?.entities));
  }, [data]);

  const reset = () => {
    search.cancel();
    setOptions([]);
  };

  return { options, search, loading, error, reset };
};

const SocialMediaFlowSettingForm = (
  props: SocialMediaFlowSettingFormProps
): JSX.Element => {
  const [selectedAllowedUsers, setSelectedAllowedUsers] = useState<
    AutoCompleteOption[]
  >([]);
  const [storeInputValue, setStoreInputValue] = useState('');
  const [storeIdInputValue, setStoreIdInputValue] = useState('');
  const [form] = Form.useForm();
  const storeNameSearch = useStoreAutocomplete(false);
  const storeIdSearch = useStoreAutocomplete(true);

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery<Record<string, SocialMediaFlowSetting>>(props.query, {
    fetchPolicy: 'cache-and-network',
  });
  const [setSettings, { loading }] = useMutation<
    Record<string, SocialMediaFlowSetting>
  >(props.mutation);

  const settings = data?.[props.queryField];
  const storesError = storeNameSearch.error || storeIdSearch.error;

  useEffect(() => {
    if (!settings) return;

    form.setFieldsValue({ isEnabled: settings.isEnabled });
    setSelectedAllowedUsers(
      settings.allowedUsers.map((user) => ({
        value: user.id,
        label:
          user.storeDetails?.storeName || `${user.firstName} ${user.lastName}`,
      }))
    );
  }, [settings, form]);

  const resetStoreSearches = () => {
    setStoreInputValue('');
    setStoreIdInputValue('');
    storeNameSearch.reset();
    storeIdSearch.reset();
  };

  const handleStoreSelect = (value: string, options: AutoCompleteOption[]) => {
    const selectedOption = options.find((option) => option.value === value);
    if (
      !selectedOption ||
      selectedAllowedUsers.some((user) => user.value === value)
    ) {
      return;
    }

    setSelectedAllowedUsers([...selectedAllowedUsers, selectedOption]);
    resetStoreSearches();
  };

  const handleSubmit = async (values: { isEnabled?: boolean }) => {
    try {
      await setSettings({
        variables: {
          input: {
            isEnabled: values.isEnabled,
            allowedUserIds: selectedAllowedUsers.map((user) => user.value),
          },
        },
      });
      await refetch();
      successNotification(`${props.title} settings saved successfully`);
    } catch (error) {
      errorNotification(`Failed to save ${props.title.toLowerCase()} settings`);
    }
  };

  if (queryLoading && !settings) {
    return <Spin size="large" />;
  }

  return (
    <section>
      <Title level={4}>{props.title}</Title>
      <Text type="secondary">{props.description}</Text>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="isEnabled" valuePropName="checked">
          <Checkbox>{props.checkboxLabel}</Checkbox>
        </Form.Item>

        <Form.Item label="Allowed Users (Whitelist)">
          <Text type="secondary" className={styles.helpText}>
            Search by first/last name or store name, or paste an exact store ID.
          </Text>
          <div className={styles.searchInputs}>
            <AutoComplete
              value={storeInputValue}
              options={storeNameSearch.options}
              onSearch={(value) => {
                setStoreInputValue(value);
                setStoreIdInputValue('');
                storeIdSearch.reset();
                storeNameSearch.search(value);
              }}
              onSelect={(value: string) =>
                handleStoreSelect(value, storeNameSearch.options)
              }
              placeholder="Search by name or store name..."
              className={styles.searchInput}
              allowClear
              notFoundContent={
                storeNameSearch.loading ? 'Searching...' : 'No stores found'
              }
            />
            <AutoComplete
              value={storeIdInputValue}
              options={storeIdSearch.options}
              onSearch={(value) => {
                setStoreIdInputValue(value);
                setStoreInputValue('');
                storeNameSearch.reset();
                storeIdSearch.search(value);
              }}
              onSelect={(value: string) =>
                handleStoreSelect(value, storeIdSearch.options)
              }
              placeholder="Search by exact store ID..."
              className={styles.searchInput}
              allowClear
              notFoundContent={
                storeIdSearch.loading ? 'Searching...' : 'No stores found'
              }
            />
          </div>
          {storesError && (
            <Text type="danger" className={styles.helpText}>
              Error searching stores: {storesError.message}
            </Text>
          )}
          <div className={styles.selectedTags}>
            {selectedAllowedUsers.map((user) => (
              <Tag
                key={user.value}
                closable
                onClose={() =>
                  setSelectedAllowedUsers(
                    selectedAllowedUsers.filter(
                      (selectedUser) => selectedUser.value !== user.value
                    )
                  )
                }
                icon={<DeleteOutlined />}
              >
                {user.label}
              </Tag>
            ))}
          </div>
          <Text type="secondary">
            Leave empty to enable the flow for everyone. Add users to restrict
            it to the whitelist.
          </Text>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Save Settings
          </Button>
        </Form.Item>
      </Form>
    </section>
  );
};

export default SocialMediaFlowSettingForm;
